import * as React from 'react';

import { cn } from '../../lib/cn';

type CellTone = 'primary' | 'muted' | 'accent' | 'success' | 'warn' | 'danger';

const TONE_CLS: Record<CellTone, string> = {
	primary: 'text-text',
	muted: 'text-text-muted',
	accent: 'text-accent',
	success: 'text-accent-green',
	warn: 'text-warn',
	danger: 'text-danger',
};

export interface MiniTableColumn<Row> {
	/** Chiave univoca della colonna. */
	key: string;
	/** Intestazione della colonna. */
	header: React.ReactNode;
	/** Allineamento del testo. Default: sinistra. */
	align?: 'left' | 'right' | 'center';
	/** Se true, usa il font monospaziato per la cella. */
	mono?: boolean;
	/** Colore del testo della cella. Default: `primary`. */
	tone?: CellTone;
	/** Se true, il testo della cella è in grassetto. */
	semibold?: boolean;
	/** Renderizza il contenuto della cella per una riga. */
	render: (row: Row) => React.ReactNode;
}

export interface MiniTableProps<Row> {
	/** Definizione delle colonne. */
	columns: MiniTableColumn<Row>[];
	/** Righe di dati. */
	rows: Row[];
	/** Estrae una chiave React univoca da una riga. */
	rowKey: (row: Row) => string | number;
	/** Mostrato al posto del corpo tabella quando `rows` è vuoto. */
	emptyMessage?: React.ReactNode;
	/** Se presente, rende le righe cliccabili e viene chiamata al click con la riga. */
	onRowClick?: (row: Row) => void;
}

/** Tabella dati compatta e bordata, con allineamento, monospazio e colore per colonna. */
function MiniTable<Row>({ columns, rows, rowKey, emptyMessage, onRowClick }: MiniTableProps<Row>) {
	if (rows.length === 0 && emptyMessage) {
		return <p className="py-4 text-center text-[13px] text-text-muted">{emptyMessage}</p>;
	}

	const alignCls = (align?: 'left' | 'right' | 'center') =>
		align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

	return (
		<div className="overflow-hidden rounded-lg border border-border">
			<table className="w-full text-[13px]">
				<thead className="bg-background/50 text-text-muted">
					<tr>
						{columns.map((col) => (
							<th key={col.key} className={cn('px-3 py-2 font-semibold', alignCls(col.align))}>
								{col.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr
							key={rowKey(row)}
							className={cn(
								'border-t border-border',
								onRowClick && 'cursor-pointer transition-colors hover:bg-accent/5',
							)}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
						>
							{columns.map((col) => (
								<td
									key={col.key}
									className={cn(
										'px-3 py-2',
										alignCls(col.align),
										col.mono && 'font-mono',
										col.semibold && 'font-semibold',
										TONE_CLS[col.tone ?? 'primary'],
									)}
								>
									{col.render(row)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
MiniTable.displayName = 'MiniTable';

export { MiniTable };
